// ─────────────────────────────────────────────
// NAS API 클라이언트 — https://api.bongnas.pe.kr
// ─────────────────────────────────────────────
// 환경변수:
//   VITE_AUTH_MODE=mock  → 임시 로컬 인증 (기본값)
//   VITE_AUTH_MODE=api   → NAS REST API + HttpOnly Secure Cookie
//   VITE_API_BASE_URL    → API 서버 주소 (기본값 https://api.bongnas.pe.kr)

const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL || 'https://api.bongnas.pe.kr';
const AUTH_MODE: 'mock' | 'api' = (import.meta.env.VITE_AUTH_MODE as 'mock' | 'api') || 'mock';
export { API_BASE_URL, AUTH_MODE };

// ── Mock 모드 상수 ──
const AUTH_TOKEN_KEY = 'family_album_token';
const AUTH_REFRESH_KEY = 'family_album_refresh';
const AUTH_USER_KEY = 'family_album_user';
const REQUEST_TIMEOUT_MS = 10000;

// ──────────────────────── 타입 ────────────────────────

export interface User {
  id?: number | string;
  name: string;
  email: string;
  role: 'admin' | 'member';
  isOwner?: boolean;
  familyId?: number | string;
  familyName?: string;
  status?: 'active' | 'pending' | 'rejected';
  createdAt?: string;
  approvedAt?: string | null;
}

interface UserResponse {
  user: User;
}

export interface AuthTokens {
  token: string;
  refreshToken: string;
}

export interface LoginPayload {
  email: string;
  password: string;
}

export interface SignupPayload {
  name: string;
  email: string;
  password: string;
  inviteCode: string;
}

export interface CredentialsUpdatePayload {
  /** 변경할 로그인 아이디(이메일). 생략하면 현재 아이디를 유지합니다. */
  email?: string;
  currentPassword: string;
  /** 새 비밀번호. 생략하면 현재 비밀번호를 유지합니다. */
  newPassword?: string;
}

// ──────────────────────── 커스텀 에러 ────────────────────────

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

export class NetworkError extends Error {
  constructor(message: string = '네트워크 연결을 확인해주세요') {
    super(message);
    this.name = 'NetworkError';
  }
}

export class TimeoutError extends Error {
  constructor(message: string = '요청 시간이 초과되었습니다') {
    super(message);
    this.name = 'TimeoutError';
  }
}

/**
 * FastAPI는 입력값 검증 실패(422) 때 detail을 문자열이 아닌 오류 목록으로
 * 내려줍니다. 그대로 Error에 넣으면 브라우저에는 "[object Object]"만 보여서
 * 사용자가 무엇을 고쳐야 하는지 알 수 없습니다.
 */
function formatApiError(body: unknown, fallback: string): string {
  const formatValue = (value: unknown): string | null => {
    if (typeof value === 'string' && value.trim()) return value.trim();

    if (Array.isArray(value)) {
      const messages = value
        .map((item) => formatValue(item))
        .filter((message): message is string => Boolean(message));
      return messages.length ? [...new Set(messages)].join(' / ') : null;
    }

    if (value && typeof value === 'object') {
      const record = value as Record<string, unknown>;
      return formatValue(record.msg)
        || formatValue(record.message)
        || formatValue(record.detail)
        || formatValue(record.error);
    }

    return null;
  };

  if (!body || typeof body !== 'object') return fallback;
  const record = body as Record<string, unknown>;
  return formatValue(record.detail)
    || formatValue(record.message)
    || formatValue(record.error)
    || fallback;
}

// ══════════════════════════════════════════════
//  MOCK 모드 — 토큰 저장소 & 요청 래퍼
// ══════════════════════════════════════════════

export const tokenStorage = {
  getToken(): string | null {
    return localStorage.getItem(AUTH_TOKEN_KEY) || sessionStorage.getItem(AUTH_TOKEN_KEY);
  },

  getRefreshToken(): string | null {
    return localStorage.getItem(AUTH_REFRESH_KEY) || sessionStorage.getItem(AUTH_REFRESH_KEY);
  },

  setTokens(tokens: AuthTokens, persistent: boolean): void {
    const storage = persistent ? localStorage : sessionStorage;
    storage.setItem(AUTH_TOKEN_KEY, tokens.token);
    storage.setItem(AUTH_REFRESH_KEY, tokens.refreshToken);
  },

  setUser(user: User): void {
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
    sessionStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
  },

  getUser(): User | null {
    const raw = localStorage.getItem(AUTH_USER_KEY) || sessionStorage.getItem(AUTH_USER_KEY);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.email) return parsed as User;
      return null;
    } catch {
      return null;
    }
  },

  clear(): void {
    [localStorage, sessionStorage].forEach((s) => {
      s.removeItem(AUTH_TOKEN_KEY);
      s.removeItem(AUTH_REFRESH_KEY);
      s.removeItem(AUTH_USER_KEY);
    });
  },
};

/** Mock 모드 전용 — Bearer 토큰을 헤더에 포함하는 fetch 래퍼 */
async function request<T>(
  endpoint: string,
  options: { method?: string; headers?: Record<string, string>; body?: string } = {},
  timeoutMs: number = REQUEST_TIMEOUT_MS,
): Promise<T> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  const token = tokenStorage.getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
      signal: controller.signal,
    });

    clearTimeout(timeout);

    if (response.status === 401) {
      tokenStorage.clear();
      throw new ApiError('인증이 만료되었습니다. 다시 로그인해주세요.', 401);
    }

    if (!response.ok) {
      let message = '서버 오류가 발생했습니다';
      try {
        const body = await response.json();
        message = formatApiError(body, message);
      } catch { /* 응답 본문 파싱 실패 시 기본 메시지 사용 */ }
      throw new ApiError(message, response.status);
    }

    return await response.json();
  } catch (err) {
    clearTimeout(timeout);
    if (err instanceof ApiError) throw err;
    if ((err as Error).name === 'AbortError') {
      throw new TimeoutError();
    }
    if (err instanceof TypeError) {
      throw new NetworkError();
    }
    throw err;
  }
}

// ══════════════════════════════════════════════
//  API 모드 — HttpOnly Secure Cookie 요청
// ══════════════════════════════════════════════

/** API 모드 전용 — credentials: 'include', Bearer 헤더 없음, 토큰 저장 안 함 */
export async function cookieRequest<T>(
  endpoint: string,
  options: { method?: string; headers?: Record<string, string>; body?: string } = {},
  timeoutMs: number = REQUEST_TIMEOUT_MS,
  unauthorizedFallback: string = '인증이 만료되었습니다. 다시 로그인해주세요.',
): Promise<T> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
      signal: controller.signal,
    });

    clearTimeout(timeout);

    if (response.status === 401) {
      // 로그인 실패, 현재 비밀번호 불일치처럼 서버가 사유를 알려 준 경우에는
      // "인증 만료"로 덮어쓰지 않고 그 안내를 그대로 보여줍니다.
      let message = unauthorizedFallback;
      try {
        const body = await response.json();
        message = formatApiError(body, message);
      } catch { /* 응답 본문 파싱 실패 시 상황별 기본 문구 사용 */ }
      throw new ApiError(message, 401);
    }

    if (response.status === 403) {
      let message = '접근 권한이 없습니다';
      try {
        const body = await response.json();
        message = formatApiError(body, message);
      } catch { /* 응답 본문 파싱 실패 시 기본 메시지 사용 */ }
      throw new ApiError(message, 403);
    }

    if (!response.ok) {
      let message = '서버 오류가 발생했습니다';
      try {
        const body = await response.json();
        message = formatApiError(body, message);
      } catch { /* 응답 본문 파싱 실패 시 기본 메시지 사용 */ }
      throw new ApiError(message, response.status);
    }

    // 204 No Content (예: 로그아웃)
    if (response.status === 204) return undefined as T;

    return await response.json();
  } catch (err) {
    clearTimeout(timeout);
    if (err instanceof ApiError) throw err;
    if ((err as Error).name === 'AbortError') {
      throw new TimeoutError();
    }
    if (err instanceof TypeError) {
      throw new NetworkError();
    }
    throw err;
  }
}

// ══════════════════════════════════════════════
//  Mock 모드 임시 함수
// ══════════════════════════════════════════════

function generateMockToken(): string {
  return 'temp_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
}

interface TempAuthResponse {
  token: string;
  refreshToken: string;
  user: User;
}

async function tempLogin(payload: LoginPayload): Promise<TempAuthResponse> {
  await new Promise((r) => setTimeout(r, 400));
  const isAdmin = payload.email === 'admin@family.com' && payload.password === 'admin123';
  return {
    token: generateMockToken(),
    refreshToken: generateMockToken(),
    user: {
      name: isAdmin ? '관리자' : payload.email.split('@')[0],
      email: payload.email,
      role: isAdmin ? 'admin' : 'member' as const,
      isOwner: isAdmin,
    },
  };
}

async function tempSignup(payload: SignupPayload): Promise<TempAuthResponse> {
  await new Promise((r) => setTimeout(r, 500));
  return {
    token: generateMockToken(),
    refreshToken: generateMockToken(),
    user: { name: payload.name, email: payload.email, role: 'member' as const },
  };
}

async function tempLogout(): Promise<void> {
  await new Promise((r) => setTimeout(r, 200));
}

// ══════════════════════════════════════════════
//  통합 인증 API — AUTH_MODE 에 따라 분기
// ══════════════════════════════════════════════

export const authApi = {
  /** 로그인 */
  async login(payload: LoginPayload, rememberMe: boolean): Promise<{ user: User }> {
    if (AUTH_MODE === 'mock') {
      const result = await tempLogin(payload);
      tokenStorage.setTokens(
        { token: result.token, refreshToken: result.refreshToken },
        rememberMe,
      );
      tokenStorage.setUser(result.user);
      return { user: result.user };
    }

    // API 모드: HttpOnly Secure Cookie — 서버가 Set-Cookie로 토큰 발급
    await cookieRequest(
      '/api/auth/login',
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      REQUEST_TIMEOUT_MS,
      '로그인에 실패했습니다. 아이디와 비밀번호를 다시 확인해 주세요.',
    );
    // 쿠키가 설정된 상태로 GET /api/auth/me 호출
    const response = await cookieRequest<UserResponse>('/api/auth/me');
    return { user: response.user };
  },

  /** 회원가입 */
  async signup(payload: SignupPayload): Promise<{ user: User }> {
    if (AUTH_MODE === 'mock') {
      const result = await tempSignup(payload);
      tokenStorage.setTokens(
        { token: result.token, refreshToken: result.refreshToken },
        true,
      );
      tokenStorage.setUser(result.user);
      return { user: result.user };
    }

    // API 모드: HttpOnly Secure Cookie
    await cookieRequest('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    const response = await cookieRequest<UserResponse>('/api/auth/me');
    return { user: response.user };
  },

  /** 현재 로그인한 사용자의 아이디·비밀번호 변경 */
  async updateCredentials(payload: CredentialsUpdatePayload): Promise<{ user: User; message: string }> {
    if (AUTH_MODE === 'mock') {
      const currentUser = tokenStorage.getUser();
      if (!currentUser) throw new ApiError('로그인이 필요합니다. 다시 로그인해 주세요.', 401);
      const user = { ...currentUser, ...(payload.email ? { email: payload.email } : {}) };
      tokenStorage.setUser(user);
      return { user, message: '로그인 정보가 변경되었습니다.' };
    }

    return cookieRequest<{ user: User; message: string }>('/api/auth/credentials', {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },

  /** 로그아웃 */
  async logout(): Promise<void> {
    if (AUTH_MODE === 'mock') {
      try {
        await tempLogout();
      } catch {
        // API 실패해도 클라이언트 토큰은 삭제
      }
      tokenStorage.clear();
      return;
    }

    // API 모드: POST /api/auth/logout → 서버가 Set-Cookie 만료로 쿠키 삭제
    try {
      await cookieRequest('/api/auth/logout', { method: 'POST' });
    } catch {
      // 로그아웃 API 실패도 허용 — 클라이언트에 저장된 토큰 없음
    }
  },

  /** 저장된 사용자 복원 (새로고침 / 재방문) */
  async restoreUser(): Promise<User | null> {
    if (AUTH_MODE === 'mock') {
      return tokenStorage.getUser();
    }

    // API 모드: GET /api/auth/me → HttpOnly 쿠키로 세션 확인
    try {
      const response = await cookieRequest<UserResponse>('/api/auth/me');
      return response.user;
    } catch {
      return null;
    }
  },

  /** 토큰 갱신 */
  async refreshToken(): Promise<AuthTokens | null> {
    if (AUTH_MODE === 'mock') {
      const refresh = tokenStorage.getRefreshToken();
      if (!refresh) return null;
      try {
        const result = await tempLogin({ email: '', password: '' });
        if (result.token) {
          const persistent = !!localStorage.getItem(AUTH_TOKEN_KEY);
          tokenStorage.setTokens(
            { token: result.token, refreshToken: result.refreshToken },
            persistent,
          );
          return { token: result.token, refreshToken: result.refreshToken };
        }
      } catch {
        tokenStorage.clear();
      }
      return null;
    }

    // API 모드: HttpOnly 쿠키 기반이므로 별도 갱신 불필요.
    // GET /api/auth/me 로 세션 유효성만 확인.
    try {
      await cookieRequest('/api/auth/me');
      return { token: '', refreshToken: '' };
    } catch {
      return null;
    }
  },

  /** 비밀번호 재설정 요청 */
  async requestPasswordReset(email: string): Promise<void> {
    if (AUTH_MODE === 'mock') {
      await new Promise((r) => setTimeout(r, 300));
      return;
    }

    await cookieRequest('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },
};

// ──────────────────────── 유틸 ────────────────────────

/** 입력값 기본 검증 (API 호출 전 클라이언트 1차 방어) */
export const validators = {
  email(value: string): string | null {
    const trimmed = value.trim();
    if (!trimmed) return '이메일을 입력해주세요';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return '올바른 이메일 형식이 아닙니다';
    return null;
  },

  password(value: string): string | null {
    const trimmed = value.trim();
    if (!trimmed) return '비밀번호를 입력해주세요';
    if (trimmed.length < 8) return '비밀번호는 8자 이상이어야 합니다';
    if (!/[A-Za-z]/.test(trimmed)) return '비밀번호에 영문을 포함해 주세요';
    if (!/\d/.test(trimmed)) return '비밀번호에 숫자를 포함해 주세요';
    return null;
  },

  name(value: string): string | null {
    const trimmed = value.trim();
    if (!trimmed) return '이름을 입력해주세요';
    if (trimmed.length < 2) return '이름은 2자 이상이어야 합니다';
    return null;
  },

  passwordConfirm(password: string, confirm: string): string | null {
    if (!confirm.trim()) return '비밀번호 확인을 입력해주세요';
    if (password !== confirm) return '비밀번호가 일치하지 않습니다';
    return null;
  },

  inviteCode(value: string): string | null {
    const trimmed = value.trim();
    if (!trimmed) return '가족 초대코드를 입력해주세요';
    if (trimmed.length !== 6 || !/^\d{6}$/.test(trimmed)) return '초대코드는 6자리 숫자입니다';
    return null;
  },
};
